import React, { useState,  useEffect } from 'react';
import styled from 'styled-components/macro';

import { distances, zIndex, colors, border, shadows, defaultRadius, palette } from '../../../styles/constants';
import { DayPicker } from '../../../components/DayPicker';
import { Button } from '../../../components/Buttons';
import { P } from '../../../components/Typography';
import { InputValidation } from '../../../components/Forms';
import { DateComponent } from '../../../components/Formatters';
import { TimePicker } from './';

interface DateTimePickerProps {
    value: {
        date: Date;
        time: Date;
    };
    setDate: (date: Date) => void;
    setTime: (time: Date) => void;
    name: string;
    label: string;
    validation?: InputValidation;
    isEdit?: boolean;
    justDate?: boolean;
    disableFuture?: boolean;
}

const DateTimePicker = (
    {
        value,
        setDate,
        setTime,
        name,
        label,
        validation,
        isEdit,
        justDate,
        disableFuture,
    }: DateTimePickerProps
) => {
    const [open, setOpen] = useState<boolean>(false);
    const [wrapperRef, setWrapperRef] = useState<any>(null);

    useEffect(() => {
        const handleClick = (event: any) => {
            if (wrapperRef && !wrapperRef.contains(event.target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    });

    return (
        <DateTimePickerWrapper isEdit={!!isEdit}>
            <P>{label}</P>
            <PickersWrapper data-just-date={justDate}>
                <StyledButton
                    type="button"
                    name={`${name}_date`}
                    onClick={() => setOpen(!open)}
                    className={validation?.state}
                >
                    <DateComponent isoString={value.date.toISOString()} />
                </StyledButton>
                {validation?.message && (
                    <Message htmlFor={`${name}_date`} className={validation?.state}>
                        {validation?.message}
                    </Message>
                )}
                <Collapsable open={open} ref={setWrapperRef}>
                    <DayPicker
                        onDayClick={(value, { disabled }) => {
                            if (disabled) return;
                            setDate(value);
                            setOpen(false);
                        }}
                        selectedDays={value.date}
                        initialMonth={new Date()}
                        {...disableFuture && {
                            disabledDays: { after: new Date() },
                        }}
                    />
                </Collapsable>
                {!justDate && <TimePickerWrapper>
                    <TimePicker
                        name={name}
                        value={value.time}
                        setTime={setTime}
                    />
                </TimePickerWrapper>}
            </PickersWrapper>
        </DateTimePickerWrapper>
    );
};

const DateTimePickerWrapper = styled.div<{ isEdit: boolean }>`
    pointer-events: ${props => props.isEdit ? 'none' : 'auto' };
    opacity: ${props => props.isEdit ? '0.6' : '1' };
    width: 45%;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const PickersWrapper = styled.div`
    position: relative;
    display: flex;
    button {
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        margin-right: -0.5px;
    }
    span {
        margin-left: -0.5px;
        border-top-right-radius: ${defaultRadius};
        border-bottom-right-radius: ${defaultRadius};
    }
    &[data-just-date='true'] {
        button {
            border-top-right-radius: ${defaultRadius};
            border-bottom-right-radius: ${defaultRadius};
            margin-right: 0;
        }
        span {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }
    }
`;

const StyledButton = styled(Button as any)`
    min-width: 0;
    flex: 1 1 150px;
    background-color: ${colors.background};
    color: ${colors.text};
    border-color: ${palette.neutral[400]};
    padding: ${distances.tiny};
    text-align: left;
    white-space: nowrap;
    margin-bottom: ${distances.small};
    min-height: 40px;

    &:hover {
        background-color: ${colors.background};
        border: ${border.normal} solid ${palette.primary[300]};
        z-index: 3;
    }

    &:focus {
        outline: none;
        border: ${border.normal} solid ${palette.primary[300]};
        box-shadow: 0 0 0 3px ${palette.primary[100]};
        background: ${colors.background};
        z-index: 3;
    }

    &.invalid,
    &:focus .invalid {
        border: ${border.normal} solid ${palette.destructive[300]};
        box-shadow: 0 0 0 3px ${palette.destructive[100]};
        z-index: 3;
    }
`;

interface CollapsableProps {
    open: boolean;
}

const Collapsable = styled.div<CollapsableProps>`
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    z-index: ${zIndex.top};
    background-color: ${colors.background};
    box-shadow: ${shadows.large};
    transition: all 0.15s ease;
    transform-origin: top center;
    border: 1px solid ${colors.borderDark};
    border-radius: ${defaultRadius};
    overflow-x: hidden;
    ${props => (props.open ? `
        transform: translateY(0);
        max-height: 400px;
        overflow-y: auto;
        opacity: 1;
        padding: ${distances.small12};
    ` : `
        transform: translateY(-1%);
        max-height: 0;
        padding-top: 0;
        padding-bottom: 0;
        border-width: 0;
        overflow-y: hidden;
        opacity: 0;
    `)};
    white-space: nowrap;
    scrollbar-width: thin;
    scrollbar-color: ${palette.neutral[200]} transparent;

    @media (max-width: 768px) {
        position: fixed;
        right: 0;
        left: 0;
        top: 100px;
        width: 90%;
        margin: 0 auto;

        z-index: ${zIndex.top}; /* keep on top of other elements on the page */
        outline: 9999px solid rgba(0, 0, 0, 0.6);
    }

    &::-webkit-scrollbar {
        width: 3px;
    }
    &::-webkit-scrollbar-thumb {
        background-color: ${palette.neutral[200]};
        border-radius: 3px;
    }
    &::-webkit-scrollbar-track {
        background-color: transparent;
    }
`;

const Message = styled.label`
    position: absolute;
    top: 37px;
    left: 0;
    font-size: 11px;
    line-height: ${distances.small};

    &.invalid {
        color: ${colors.invalid};
    }

    &.valid {
        color: ${colors.valid};
    }
`;

const TimePickerWrapper = styled.div`
    flex: 1 1 75px;
`;

export default DateTimePicker;
